import { Component, Input, ViewEncapsulation, ChangeDetectionStrategy, SimpleChanges } from "@angular/core";
import { isPlatformServer } from "@angular/common";
import { BarVertical2DComponent, DataItem, calculateViewDimensions, ChartCommonModule, AxesModule } from "@swimlane/ngx-charts";
import { CurrencyPipe } from "@angular/common"
import { environment } from "../../../environments/environment";
import { KilowantColorHelper } from "./KilowantColorHelper"
import { scaleBand } from 'd3-scale';
import { KilowantSeriesVerticalComponent } from "./kilowant-series-vertical.component";

@Component({
    selector: "kilowant-charts-home-one-offer",
    templateUrl: "../../../html/chart/kilowant-charts-home-one-offer.html",
    styleUrls: ["../../../css/base-chart.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ChartCommonModule, AxesModule, KilowantSeriesVerticalComponent]
})

export class KilowantBarHomeOneOfferChartComponent extends BarVertical2DComponent {

    @Input() savings: number[];
    savingsPositions: number[];
    savingsPositionsInUSD: number[];

    @Input() showSavingsInUSD: boolean;

    @Input() savingsInUSD: number[];

    @Input() datasetSelector: boolean = false;
    @Input() selectedDatasets: number[] = [];

    @Input() utilityAverageRate: number = 0;
    @Input() communityAverageRate: number = 0;

    @Input() prefix: string = "";

    group: any;

    BIG_MAX: number = 5;

    yScaleMin: number | null = null;

    countColumns: number;

    colorHelper: KilowantColorHelper;
    legendData: string[] = [];

    currentShift: number = 0;
    currentZoom: number = 1;

    utilityName: string = 'RI Energy';

    isSSR = false;
    initiated: boolean = false;

    RIColor: string = "#" + environment.RI_UTILITY_COLOR;
    RIEstimatedColor: string = "#" + environment.RI_UTILITY_FORECASTED_COLOR;
    CommunityColor: string = "#" + environment.RI_COMMUNITY_COLOR;
    CommunityEstimatedColor: string = "#" + environment.RI_COMMUNITY_FORECASTED_COLOR;
    kilowantColor: string = "#" + environment.KILOWANT_COLOR;

    astericsPositions: number[] = [];

    ngOnInit() {
        if (isPlatformServer(this.platformId)) {
            this.isSSR = true;
        }
        this.group = this.results[0];
        this.margin = [0, 0, 0, 0];
        this.animations = false;
        this.groupPadding = 0;
        this.colorHelper = new KilowantColorHelper(this.colors, this.scheme, this.schemeType, ["RI Energy", "RI Energy Last Resort Service", "RI Energy (Projected)", "NextEra Energy", "NextEra Energy (Projected)", "Kilowant"], [this.RIColor, this.RIColor, this.RIEstimatedColor, this.CommunityColor, this.CommunityEstimatedColor, this.kilowantColor]);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.results == null || this.results.length === 0 || this.results[0] == null) {
            return;
        }
        this.group = this.results[0];
        this.margin = [0, 0, 0, 0];
        this.update();
    }

    update(): void {
        if (this.group == null || this.group.series == null) {
            return;
        }
        this.countColumns = 1;
        this.yScaleMin = null;

        this.valueDomain = this.getValueDomain();
        if (this.yScaleMax == 0 && this.valueDomain[1] == 0) {
            this.yScaleMax = this.BIG_MAX;
        }
        super.update();
        this.dims = calculateViewDimensions({
            width: this.width,
            height: this.height,
            margins: this.margin,
            showXAxis: this.xAxis,
            showYAxis: this.yAxis,
            xAxisHeight: this.xAxisHeight,
            yAxisWidth: this.yAxisWidth,
            showXLabel: this.showXAxisLabel,
            showYLabel: this.showYAxisLabel,
            showLegend: this.legend,
            legendType: this.schemeType,
            legendPosition: this.legendPosition
        });
        this.transform = `translate(0, 20)`;
    }

    getInnerScale(): any {
        const width = this.width;
        const spacing = this.innerDomain.length / (width / this.barPadding + 1);
        let innerDomain = this.innerDomain;
        let totalElements = innerDomain.length;
        let initModified = function () {
            let d3 = scaleBand().rangeRound([0, width]).paddingInner(spacing).domain(innerDomain);
            function d3Based(label: string) {
                return d3(label);
            }
            d3Based.bandwidth = function () {
                return d3.bandwidth();
            }
            return d3Based;
        }
        return initModified();
    }

    getValueDomain(): [number, number] {
        const domain: number[] = [];
        if (this.group == null || this.group.series == null) {
            return [0, this.yScaleMax ? this.yScaleMax : this.BIG_MAX];
        }
        for (const d of this.group.series) {
            if (!domain.includes(d.value) && d.value > 0) {
                domain.push(d.value);
            }
        }

        const min = this.yScaleMin ? Math.min(this.yScaleMin, ...domain) * 0.8 : Math.min(...domain) * 0.8;
        const max = this.yScaleMax ? Math.max(this.yScaleMax, ...domain) : Math.max(...domain);
        return [min, max];
    }

    getSeries(): DataItem[] {
        var series: DataItem[] = [];
        if (this.group == null || this.group.series == null) {
            return series;
        }
        for (let i = 0; i < this.group.series.length; i++) {
            var im = Object.assign(new Object(), this.group.series[i]);
            if (this.group.series[i].isForecasted) {
                im.name = im.name + " (Projected)";
                im.label = im.label + " (Projected)";
            }
            series.push(im);
        }
        return series;
    }

    yAxisFormat(val: number): string | null {
        return (new CurrencyPipe("en-US")).transform(val, "USD", "symbol", "1.3-3");
    }
}